// The driver detail payload intentionally keeps completed prescriptions in the
// list as history. They remain visible, but must never be selected, validated,
// or submitted as a new delivery.

// Do not infer this from the parent delivery_status. A multi-dose prescription
// can have a delivered parent while later child doses are still pending. The
// backend's parent + child roll-up is the authoritative completion signal.
export const isRxFullyDelivered = (rx) => Boolean(rx?.is_fully_delivered)

export const isDoseDelivered = (dose) =>
	String(dose?.delivery_status || '').toLowerCase() === 'delivered'

export const canSubmitRxForDelivery = (rx) =>
	!isRxFullyDelivered(rx) && rx?.rx_access?.can_deliver !== false

export const isRxSelectableForDelivery = (rx) => {
	if (!canSubmitRxForDelivery(rx)) return false

	// Preserve the existing multi-dose behavior: the parent may already say
	// delivered while a later child dose is still actionable.
	if (String(rx?.delivery_status || '').toLowerCase() === 'delivered') return true

	const doses = Array.isArray(rx?.investigate_dilaudid_delivery_all)
		? rx.investigate_dilaudid_delivery_all
		: []

	return doses.length === 0 || !doses.every((dose) => dose?.can_deliver === false)
}

export const getDefaultDeliverRxIds = (rxData) =>
	(Array.isArray(rxData) ? rxData : [])
		.filter(isRxSelectableForDelivery)
		.map((rx) => rx?.id)
		.filter((id) => id !== null && id !== undefined)

// Apply the same safety check again while building the request. This protects
// against stale component state after a live route/handover update.
export const withoutNonDeliverableRxIds = (selectedIds, rxData) => {
	const ids = Array.isArray(selectedIds) ? selectedIds : []
	const rows = Array.isArray(rxData) ? rxData : []
	if (ids.length === 0 || rows.length === 0) return ids

	const rowsById = new Map(rows.map((rx) => [String(rx?.id), rx]))

	return ids.filter((id) => {
		const rx = rowsById.get(String(id))
		return !rx || isRxSelectableForDelivery(rx)
	})
}

// Preserve the distinction between no explicit selection and an explicit
// selection that becomes empty after delivered/handed-over rows are removed.
// The backend uses this marker to avoid its legacy whole-patient fallback.
export const buildDeliverRxIdsPayload = (selectedIds, rxData) => {
	if (!Array.isArray(selectedIds) || selectedIds.length === 0) return {}

	return {
		deliver_rx_ids: withoutNonDeliverableRxIds(selectedIds, rxData),
		has_selected_deliver_rx_ids: true,
	}
}

// Missed-route arrays contain only missed doses. Merge delivered doses back for
// display only; the dose selector still prevents delivered doses from being
// selected or submitted.
export const mergeDeliveredDosesForDisplay = (routeDoses, allDoses) => {
	const routeRows = Array.isArray(routeDoses) ? routeDoses : []
	const everyDose = Array.isArray(allDoses) ? allDoses : []
	if (routeRows.length === 0 || everyDose.length === 0) return routeDoses

	const routeIds = new Set(routeRows.map((dose) => dose?.id))
	const deliveredOnly = everyDose.filter(
		(dose) => isDoseDelivered(dose) && !routeIds.has(dose?.id)
	)
	if (deliveredOnly.length === 0) return routeDoses

	return [...deliveredOnly, ...routeRows].sort(
		(a, b) => (Number(a?.dose_no) || 0) - (Number(b?.dose_no) || 0)
	)
}
